/**
 * Pharmacy finance.
 *
 * Three different questions, kept on three different shelves of the page:
 * what the pharmacy EARNED (revenue, cost of goods, profit), what CASH came in
 * and went out, and what it SPENT on stock — which is inventory, not an
 * expense, and reaches profit only when the goods are sold. Every figure is
 * the API's; this screen formats it.
 */

import { useState, type ReactNode } from 'react';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type PaymentMethod } from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { dateTime, money, todayIn } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Modal, Select, Spinner, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { newKey, usePharmacy, usePharmacyWrite, type ListOf, type Valuation } from './data';
import { ErrorLine, StatusBadge, Table, Td, Tile } from './parts';
import { PendingDecision, type PendingDecisionBody } from './PendingDecision';
import { TransferMatchingSection } from './TransferMatching';
import { TRANSFER_REFERENCE_HINT, needsTransferReference, transferReferenceReady } from '../../lib/till';

interface FinanceReport {
  period: { from: string; to: string; timezone: string };
  currencyCode: string;
  sales: { count: number; revenue: string; discountsGiven: string; refunds: string; netRevenue: string };
  costOfGoodsSold: { atSale: string; reversedForRestockedReturns: string; net: string; coverage: 'complete' | 'partial'; uncostedRevenue: string };
  basis: string;
  grossProfit: string | null;
  grossProfitCoverage: number | null;
  operatingExpenses: string;
  operatingProfit: string;
  stockLosses: { total: string; byReason: Record<string, string> };
  netProfit: string;
  profitAfterStockLosses: string | null;
  reconciliation: { matches: boolean; lines: Array<{ figure: string; reports: string; pharmacyLedgers: string; difference: string }> };
  cash: { collectedFromSales: string; collectedOnOpeningBalances: string; refundsPaidOut: string; netCollected: string; pendingConfirmation: string };
  inventory: { purchasesReceived: string; paidToSuppliers: string; note: string };
  warnings: string[];
  customerDebts: { rows: Array<{ customerId: string | null; name: string; fromSales: string; fromOpeningBalance: string; total: string }>; total: string };
  supplierBalances: Array<{ supplierId: string; name: string; opening: string; invoiced: string; paid: string; credits: string; balance: string }>;
  stockValuation: Valuation;
}

interface SaleRow {
  id: string;
  reference: string;
  occurred_at: string;
  amount: string;
  payment_status: string;
  customer_name: string | null;
  cashier: string | null;
  branch_name: string;
  paid: string;
  pending: string;
}

interface ReceiptLine {
  sale_item_id: string;
  description: string;
  unit_name: string;
  unit_quantity: number;
  line_total: string;
}

interface Receipt {
  reference: string;
  amount: string;
  amountPaid: string;
  outstanding: string;
  paymentStatus: string;
  lines: ReceiptLine[];
  payments: Array<{ method: string; amount: string; paid_at: string }>;
  pendingPayments: Array<{ id: string; method: string; amount: string; status: string }>;
  refunds: Array<{ reference: string; amount: string }>;
}

interface RefundLine {
  line_id: string;
  refund_id: string;
  reference: string;
  sale_reference: string;
  refunded_at: string;
  refunded_by: string | null;
  product_name: string;
  batch_number: string | null;
  base_quantity: number;
  amount: string;
  goods_returned: boolean;
  disposition: string;
  reason: string;
}

interface TillRow {
  id: string;
  branch_name: string;
  cashier: string;
  status: string;
  opened_at: string;
  closed_at: string | null;
  opening_float: string;
  counted_cash: string | null;
  expected_cash: string | null;
  variance: string | null;
}

type Section = 'report' | 'sales' | 'refunds' | 'tills' | 'transfers';

const methodLabel = (m: string) => PAYMENT_METHOD_LABELS[m as PaymentMethod] ?? m;

export function PharmacyFinancePage(): ReactNode {
  const { can } = useAuth();
  const sections: Array<{ key: Section; label: string; allowed: boolean }> = [
    { key: 'report', label: 'Profit & cash', allowed: can('reports:read') },
    { key: 'sales', label: 'Sales', allowed: can('pharmacy:sell') || can('sales:read') },
    { key: 'refunds', label: 'Refunds & returns', allowed: can('pharmacy:refund') || can('pharmacy:disposition') },
    { key: 'tills', label: 'Tills', allowed: can('pharmacy:till') || can('cash:reconcile') },
    { key: 'transfers', label: 'Bank transfers', allowed: can('cash:reconcile') },
  ];
  const visible = sections.filter((s) => s.allowed);
  const [section, setSection] = useState<Section>(visible[0]?.key ?? 'report');

  return (
    <div className="space-y-5">
      <PageHeader title="Finance" description="Revenue and profit, cash collected, refunds and tills — each stated separately" />
      <div role="tablist" aria-label="Finance sections" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {visible.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={section === s.key}
              onClick={() => setSection(s.key)}
              className={cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium',
                section === s.key ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {visible.length === 0 && <p className="text-sm text-slate-500">You do not have access to any finance screen.</p>}
      {section === 'report' && can('reports:read') && <ReportSection />}
      {section === 'sales' && <SalesSection />}
      {section === 'refunds' && <RefundsSection />}
      {section === 'tills' && <TillsSection />}
      {section === 'transfers' && can('cash:reconcile') && <TransferMatchingSection />}
    </div>
  );
}

// ── Profit & cash ──────────────────────────────────────────────────────────

function ReportSection(): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const { branchId } = useWorkingLocation();
  const report = usePharmacy<FinanceReport>('/reports/finance', { from, to, branchId });
  const r = report.data;
  const c = r?.currencyCode ?? business?.currencyCode ?? 'NGN';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:w-80">
        <Input label="From" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <Input label="To" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
      </div>

      {report.isError ? (
        <ErrorState message="Could not load the finance report." onRetry={() => void report.refetch()} />
      ) : !r ? (
        <Spinner label="Loading report" />
      ) : (
        <>
          {r.warnings.length > 0 && (
            <ul className="space-y-1.5">
              {r.warnings.map((w) => (
                <li key={w} role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {w}
                </li>
              ))}
            </ul>
          )}

          <section aria-label="Profit" className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Profit</h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Tile label="Revenue" value={money(r.sales.revenue, c)} sub={`${r.sales.count} sales`} />
              <Tile label="Discounts given" value={money(r.sales.discountsGiven, c)} sub="already off revenue" />
              <Tile label="Refunds" value={money(r.sales.refunds, c)} sub="given in the period" tone={Number(r.sales.refunds) > 0 ? 'warn' : 'plain'} />
              <Tile label="Net revenue" value={money(r.sales.netRevenue, c)} sub="revenue − refunds" />
              <Tile
                label="Cost of goods sold"
                value={money(r.costOfGoodsSold.net, c)}
                sub={`${money(r.costOfGoodsSold.atSale, c)} at sale − ${money(r.costOfGoodsSold.reversedForRestockedReturns, c)} restocked returns`}
              />
              <Tile
                label="Gross profit"
                value={r.grossProfit === null ? 'Not stated' : money(r.grossProfit, c)}
                sub={
                  r.grossProfit === null
                    ? 'no sale in the period carries cost'
                    : r.costOfGoodsSold.coverage === 'complete'
                      ? 'net revenue − cost of goods'
                      : `over the ${r.grossProfitCoverage ?? 0}% of revenue that carries cost`
                }
                tone={r.grossProfit === null ? 'warn' : Number(r.grossProfit) >= 0 ? 'good' : 'bad'}
              />
              <Tile label="Operating expenses" value={money(r.operatingExpenses, c)} sub="from Expenses" />
              <Tile
                label="Operating profit"
                value={money(r.operatingProfit, c)}
                sub="net revenue − expenses · as Reports and the dashboard state it"
                tone={Number(r.operatingProfit) >= 0 ? 'good' : 'bad'}
              />
              <Tile label="Net profit" value={money(r.netProfit, c)} sub="operating profit ± loan lines · as Reports" />
              <Tile
                label="Stock losses"
                value={money(r.stockLosses.total, c)}
                sub={Object.entries(r.stockLosses.byReason).map(([k, v]) => `${k.replace(/_/g, ' ')} ${money(v, c)}`).join(' · ') || 'expiry, damage, write-offs'}
                tone={Number(r.stockLosses.total) > 0 ? 'warn' : 'plain'}
              />
              <Tile
                label="After cost of goods and losses"
                value={r.profitAfterStockLosses === null ? 'Not stated' : money(r.profitAfterStockLosses, c)}
                sub="gross profit − expenses − stock losses · pharmacy view, beyond Reports"
                tone={r.profitAfterStockLosses === null ? 'warn' : Number(r.profitAfterStockLosses) >= 0 ? 'good' : 'bad'}
              />
            </div>
          </section>

          <section aria-label="Reconciliation" className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Reconciliation with Reports</h2>
            <div className={cn('rounded-xl border p-3 text-sm', r.reconciliation.matches ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800')}>
              <p className="font-medium">
                {r.reconciliation.matches
                  ? 'These figures equal Reports and the dashboard for the same dates, location and currency.'
                  : 'These figures differ from the pharmacy ledgers. Investigate before relying on them.'}
              </p>
              <p className="mt-1 text-xs opacity-80">{r.basis}</p>
              {!r.reconciliation.matches && (
                <ul className="mt-2 space-y-0.5 text-xs">
                  {r.reconciliation.lines.filter((l) => Number(l.difference) !== 0).map((l) => (
                    <li key={l.figure}>{l.figure}: Reports {money(l.reports, c)} · ledgers {money(l.pharmacyLedgers, c)} · difference {money(l.difference, c)}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section aria-label="Cash" className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Cash — money in and out, not revenue</h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Tile label="From sales" value={money(r.cash.collectedFromSales, c)} sub="by date received" />
              <Tile label="On opening balances" value={money(r.cash.collectedOnOpeningBalances, c)} sub="migrated debts paid" />
              <Tile label="Refunds paid out" value={money(r.cash.refundsPaidOut, c)} />
              <Tile label="Net collected" value={money(r.cash.netCollected, c)} tone="good" />
              <Tile label="Pending confirmation" value={money(r.cash.pendingConfirmation, c)} sub="card/transfer not yet confirmed — not counted" tone={Number(r.cash.pendingConfirmation) > 0 ? 'warn' : 'plain'} />
            </div>
          </section>

          <section aria-label="Inventory" className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Inventory</h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Tile label="Purchases received" value={money(r.inventory.purchasesReceived, c)} sub="at cost" />
              <Tile label="Paid to suppliers" value={money(r.inventory.paidToSuppliers, c)} />
              <Tile label="Stock at cost" value={money(r.stockValuation.total, c)} sub={`${money(r.stockValuation.saleable, c)} saleable`} />
              <Tile label="In transit" value={money(r.stockValuation.inTransit, c)} sub={`${money(r.stockValuation.quarantinedOrRecalled, c)} held · ${money(r.stockValuation.expired, c)} expired`} />
            </div>
            <p className="text-xs text-slate-500">{r.inventory.note}</p>
            {r.stockValuation.awaitingVerification && (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Tile
                  label={r.stockValuation.awaitingVerification.label}
                  value={money(r.stockValuation.awaitingVerification.value, c)}
                  sub={`${r.stockValuation.awaitingVerification.products} item(s), ${r.stockValuation.awaitingVerification.units} unit(s) · not for sale`}
                />
                <Tile label="Inventory incl. counted stock" value={money(r.stockValuation.totalIncludingAwaiting ?? r.stockValuation.total, c)} />
              </div>
            )}
            {r.stockValuation.uncostedItems > 0 && (
              <p className="text-xs text-amber-700">{r.stockValuation.uncostedItems} item(s) on hand have no cost recorded; they are not in these values.</p>
            )}
          </section>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card padded={false}>
              <div className="p-4 sm:p-5">
                <CardHeader title="Customer debts" subtitle={`Owed to the pharmacy: ${money(r.customerDebts.total, c)}`} />
              </div>
              {r.customerDebts.rows.length === 0 ? (
                <p className="px-5 pb-6 text-sm text-slate-500">Nobody owes anything.</p>
              ) : (
                <Table head={['Customer', 'From sales', 'Opening balance', 'Total']} minWidth={480}>
                  {r.customerDebts.rows.map((d) => (
                    <tr key={d.customerId ?? d.name}>
                      <Td>{d.name}</Td>
                      <Td className="tabular-nums">{money(d.fromSales, c)}</Td>
                      <Td className="tabular-nums">{money(d.fromOpeningBalance, c)}</Td>
                      <Td className="text-right font-medium tabular-nums">{money(d.total, c)}</Td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
            <Card padded={false}>
              <div className="p-4 sm:p-5">
                <CardHeader title="Supplier balances" subtitle="What the pharmacy owes, from invoices, payments and returns" />
              </div>
              {r.supplierBalances.length === 0 ? (
                <p className="px-5 pb-6 text-sm text-slate-500">No supplier balances.</p>
              ) : (
                <Table head={['Supplier', 'Invoiced', 'Paid', 'Credits', 'Balance']} minWidth={520}>
                  {r.supplierBalances.map((s) => (
                    <tr key={s.supplierId}>
                      <Td>{s.name}</Td>
                      <Td className="tabular-nums">{money(s.invoiced, c)}</Td>
                      <Td className="tabular-nums">{money(s.paid, c)}</Td>
                      <Td className="tabular-nums">{money(s.credits, c)}</Td>
                      <Td className="text-right font-medium tabular-nums">{money(s.balance, c)}</Td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          </div>

          <p className="text-xs text-slate-500">
            Days run in {r.period.timezone}. Figures are in {c}. Cost of goods is what the sold batches cost when sold; a later
            purchase price never changes it.
          </p>
        </>
      )}
    </div>
  );
}

// ── Sales ──────────────────────────────────────────────────────────────────

function useSales(unpaidOnly: boolean, search = '') {
  const { branchId } = useWorkingLocation();
  return usePharmacy<ListOf<SaleRow>>('/sales', { branchId, unpaidOnly: unpaidOnly || undefined, search: search.trim() || undefined });
}

function SalesSection(): ReactNode {
  const { business, can } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'UTC';
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const sales = useSales(unpaidOnly);
  const [open, setOpen] = useState<SaleRow | null>(null);

  return (
    <Card padded={false}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
        <CardHeader title="Pharmacy sales" subtitle="Most recent first" />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={unpaidOnly} onChange={(e) => setUnpaidOnly(e.target.checked)} /> Owing only
        </label>
      </div>
      {sales.isError ? (
        <ErrorState message="Could not load sales." onRetry={() => void sales.refetch()} />
      ) : !sales.data ? (
        <Spinner />
      ) : sales.data.data.length === 0 ? (
        <p className="px-5 pb-6 text-sm text-slate-500">No sales.</p>
      ) : (
        <Table head={['Sale', 'When', 'Customer', 'Amount', 'Paid', 'Pending', 'Status']} minWidth={720}>
          {sales.data.data.map((s) => (
            <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(s)}>
              <Td className="font-medium text-brand-700">{s.reference}</Td>
              <Td>{dateTime(s.occurred_at, tz)}</Td>
              <Td>{s.customer_name ?? '—'}</Td>
              <Td className="tabular-nums">{money(s.amount, c)}</Td>
              <Td className="tabular-nums">{money(s.paid, c)}</Td>
              <Td className="tabular-nums">{Number(s.pending) > 0 ? money(s.pending, c) : '—'}</Td>
              <Td><StatusBadge status={s.payment_status} /></Td>
            </tr>
          ))}
        </Table>
      )}
      {open && <SaleModal sale={open} onClose={() => setOpen(null)} currency={c} canPay={can('pharmacy:sell')} />}
    </Card>
  );
}

function SaleModal({ sale, onClose, currency, canPay }: { sale: SaleRow; onClose: () => void; currency: string; canPay: boolean }): ReactNode {
  const receipt = usePharmacy<Receipt>(`/sales/${sale.id}/receipt`);
  const pay = usePharmacyWrite<{ amount: string; method: string; reference?: string; confirmed: boolean; idempotencyKey: string }>('post', `/sales/${sale.id}/payments`);
  const decide = usePharmacyWrite<PendingDecisionBody>('post', (b) => `/pending-payments/${b.id}/decide`);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [confirmed, setConfirmed] = useState(false);
  const [reference, setReference] = useState('');
  // A confirmed transfer names its alert's reference; unconfirmed, it waits as pending.
  const referenceMissing = confirmed && needsTransferReference(method) && !transferReferenceReady(reference);
  const [key, setKey] = useState(newKey);
  const r = receipt.data;

  return (
    <Modal open onClose={onClose} title={`Sale ${sale.reference}`} size="lg">
      {!r ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          <Table head={['Item', 'Qty', 'Total']} minWidth={320}>
            {r.lines.map((l) => (
              <tr key={l.sale_item_id}>
                <Td>{l.description}</Td>
                <Td className="tabular-nums">{l.unit_quantity} {l.unit_name}</Td>
                <Td className="text-right tabular-nums">{money(l.line_total, currency)}</Td>
              </tr>
            ))}
          </Table>
          <dl className="grid grid-cols-3 gap-2">
            <div><dt className="text-xs text-slate-500">Amount</dt><dd className="font-semibold tabular-nums">{money(r.amount, currency)}</dd></div>
            <div><dt className="text-xs text-slate-500">Paid</dt><dd className="font-semibold tabular-nums">{money(r.amountPaid, currency)}</dd></div>
            <div><dt className="text-xs text-slate-500">Owing</dt><dd className="font-semibold tabular-nums">{money(r.outstanding, currency)}</dd></div>
          </dl>
          {r.pendingPayments.filter((p) => p.status === 'pending').map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <span>Pending {methodLabel(p.method)} {money(p.amount, currency)}</span>
              {canPay && (
                <PendingDecision payment={p} busy={decide.isPending} onDecide={(body) => decide.mutate(body)} />
              )}
            </div>
          ))}
          <ErrorLine error={decide.error} />
          {canPay && Number(r.outstanding) > 0 && (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <p className="font-medium text-slate-900">Take a payment</p>
              <div className="grid grid-cols-2 gap-2">
                <Input label="Amount" inputMode="decimal" placeholder={r.outstanding} value={amount} onChange={(e) => { setAmount(e.target.value); setKey(newKey()); }} />
                <Select
                  label="Method"
                  value={method}
                  options={PAYMENT_METHODS.filter((m) => m !== 'credit').map((m) => ({ value: m, label: methodLabel(m) }))}
                  onChange={(e) => { setMethod(e.target.value as PaymentMethod); setConfirmed(false); setKey(newKey()); }}
                />
              </div>
              {method !== 'cash' && (
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                  I have seen the approval — otherwise it is recorded as pending
                </label>
              )}
              {method !== 'cash' && (
                <Input
                  label={needsTransferReference(method) ? 'Transfer reference' : 'Reference (optional)'}
                  placeholder={needsTransferReference(method) ? 'Session ID or reference on the bank alert' : undefined}
                  hint={needsTransferReference(method) ? TRANSFER_REFERENCE_HINT : undefined}
                  value={reference}
                  onChange={(e) => { setReference(e.target.value); setKey(newKey()); }}
                />
              )}
              <ErrorLine error={pay.error} />
              <Button
                size="sm"
                loading={pay.isPending}
                disabled={!amount || referenceMissing}
                onClick={() =>
                  pay.mutate(
                    { amount, method, confirmed: method === 'cash' || confirmed, idempotencyKey: key, ...(reference.trim() ? { reference: reference.trim() } : {}) },
                    { onSuccess: () => { setAmount(''); setReference(''); setKey(newKey()); } },
                  )
                }
              >
                Record payment
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Refunds & returns ──────────────────────────────────────────────────────

function RefundsSection(): ReactNode {
  const { business, can } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'UTC';
  const refunds = usePharmacy<ListOf<RefundLine>>('/refunds');
  const [issuing, setIssuing] = useState(false);
  const pending = (refunds.data?.data ?? []).filter((l) => l.disposition === 'pending');

  return (
    <div className="space-y-5">
      <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
        A refund returns money. It never puts a medicine back on the shelf: returned stock waits here until a pharmacist
        decides to restock it, dispose of it, or return it to the supplier.
      </p>
      {can('pharmacy:refund') && (
        <div>
          <Button onClick={() => setIssuing(true)}>Issue a refund</Button>
        </div>
      )}

      {can('pharmacy:disposition') && (
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader title="Returned stock awaiting a decision" subtitle={`${pending.length} line(s)`} />
          </div>
          {pending.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-500">Nothing is waiting.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {pending.map((l) => <DispositionRow key={l.line_id} line={l} currency={c} />)}
            </ul>
          )}
        </Card>
      )}

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Refund history" />
        </div>
        {!refunds.data ? (
          <Spinner />
        ) : refunds.data.data.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">No refunds.</p>
        ) : (
          <Table head={['Refund', 'Sale', 'When', 'Item', 'Qty', 'Amount', 'Returned', 'Stock']} minWidth={820}>
            {refunds.data.data.map((l) => (
              <tr key={l.line_id}>
                <Td className="font-medium">{l.reference}</Td>
                <Td>{l.sale_reference}</Td>
                <Td>{dateTime(l.refunded_at, tz)}</Td>
                <Td>{l.product_name}{l.batch_number && <span className="block text-xs text-slate-500">batch {l.batch_number}</span>}</Td>
                <Td className="tabular-nums">{l.base_quantity}</Td>
                <Td className="tabular-nums">{money(l.amount, c)}</Td>
                <Td>{l.goods_returned ? 'Yes' : 'No'}</Td>
                <Td><StatusBadge status={l.disposition} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {issuing && <RefundModal onClose={() => setIssuing(false)} currency={c} />}
    </div>
  );
}

function DispositionRow({ line, currency }: { line: RefundLine; currency: string }): ReactNode {
  const [note, setNote] = useState('');
  const decide = usePharmacyWrite<{ disposition: string; note: string }>('post', `/refund-lines/${line.line_id}/disposition`);
  return (
    <li className="space-y-2 px-4 py-3 sm:px-5">
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <span className="font-medium text-slate-900">
          {line.product_name} · {line.base_quantity} base units{line.batch_number ? ` · batch ${line.batch_number}` : ''}
        </span>
        <span className="text-slate-500">{line.reference} on {line.sale_reference} · {money(line.amount, currency)}</span>
      </div>
      <p className="text-xs text-slate-500">Reason: {line.reason}</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Input label="Decision note" placeholder="e.g. Seal intact, inspected" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['restock', 'dispose', 'return_to_supplier'] as const).map((d) => (
            <Button
              key={d}
              size="sm"
              variant={d === 'restock' ? 'secondary' : d === 'dispose' ? 'danger' : 'ghost'}
              disabled={note.trim().length < 3}
              loading={decide.isPending}
              onClick={() => decide.mutate({ disposition: d, note })}
            >
              {d === 'restock' ? 'Restock' : d === 'dispose' ? 'Dispose' : 'Return to supplier'}
            </Button>
          ))}
        </div>
      </div>
      <ErrorLine error={decide.error} />
    </li>
  );
}

function RefundModal({ onClose, currency }: { onClose: () => void; currency: string }): ReactNode {
  const [search, setSearch] = useState('');
  const sales = useSales(false, search);
  const [saleId, setSaleId] = useState('');
  const receipt = usePharmacy<Receipt>(`/sales/${saleId}/receipt`, {}, Boolean(saleId));
  const [qty, setQty] = useState<Record<string, string>>({});
  const [returned, setReturned] = useState<Record<string, boolean>>({});
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reason, setReason] = useState('');
  const [key] = useState(newKey);
  const refund = usePharmacyWrite<Record<string, unknown>, { reference: string; amount: string; creditAmount?: string; value?: string; duplicate: boolean }>('post', '/refunds');

  const lines = (receipt.data?.lines ?? [])
    .map((l) => ({ l, n: Number.parseInt(qty[l.sale_item_id] ?? '', 10) }))
    .filter(({ n }) => Number.isInteger(n) && n > 0);

  return (
    <Modal open onClose={onClose} title="Issue a refund" size="lg">
      {refund.data ? (
        <div className="space-y-3 text-sm">
          <p>
            Refund <span className="font-semibold">{refund.data.reference}</span> recorded: {money(refund.data.amount, currency)} paid back
            {Number(refund.data.creditAmount ?? 0) > 0 && (
              <>, and {money(refund.data.creditAmount, currency)} credited against what the customer still owed (a credit note — no money moves)</>
            )}.
            {refund.data.duplicate && ' (Already recorded — nothing was refunded twice.)'}
          </p>
          <p className="text-slate-500">Any returned stock is waiting for a pharmacist’s decision; it is not back on sale.</p>
          <Button onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Input label="Find the sale" placeholder="Reference (SAL-…) or customer name" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select
            label="Sale"
            value={saleId}
            placeholder="Choose the original sale"
            options={(sales.data?.data ?? []).map((s) => ({ value: s.id, label: `${s.reference} · ${money(s.amount, currency)}${s.customer_name ? ` · ${s.customer_name}` : ''}` }))}
            onChange={(e) => { setSaleId(e.target.value); setQty({}); setReturned({}); }}
          />
          {receipt.data && (
            <>
              <div className="space-y-2">
                {receipt.data.lines.map((l) => (
                  <div key={l.sale_item_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm">
                    <span className="min-w-0 flex-1">{l.description} <span className="text-slate-500">· sold {l.unit_quantity} {l.unit_name}</span></span>
                    <input
                      aria-label={`Refund quantity of ${l.description}`}
                      inputMode="numeric"
                      placeholder="0"
                      className="input-base h-8 w-16 text-center"
                      value={qty[l.sale_item_id] ?? ''}
                      onChange={(e) => setQty((q) => ({ ...q, [l.sale_item_id]: e.target.value.replace(/\D/g, '') }))}
                    />
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={returned[l.sale_item_id] ?? false}
                        onChange={(e) => setReturned((r) => ({ ...r, [l.sale_item_id]: e.target.checked }))}
                      />
                      Goods returned
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Paid {money(receipt.data.amountPaid, currency)}
                {receipt.data.refunds.length > 0 && <> · already refunded {receipt.data.refunds.map((x) => money(x.amount, currency)).join(', ')}</>}
              </p>
            </>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select
              label="Refund by"
              value={method}
              options={PAYMENT_METHODS.filter((m) => m !== 'credit').map((m) => ({ value: m, label: methodLabel(m) }))}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            />
            <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <ErrorLine error={refund.error} />
          <Button
            loading={refund.isPending}
            disabled={!saleId || lines.length === 0 || reason.trim().length < 3}
            onClick={() =>
              refund.mutate({
                saleId,
                method,
                reason,
                lines: lines.map(({ l, n }) => ({ saleItemId: l.sale_item_id, unitQuantity: n, goodsReturned: returned[l.sale_item_id] ?? false })),
                idempotencyKey: key,
              })
            }
          >
            Refund
          </Button>
        </div>
      )}
    </Modal>
  );
}

// ── Tills ──────────────────────────────────────────────────────────────────

function TillsSection(): ReactNode {
  const { business, can } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'UTC';
  const { branchId } = useWorkingLocation();
  const tills = usePharmacy<ListOf<TillRow>>('/tills', { branchId });
  const [reconciling, setReconciling] = useState<TillRow | null>(null);

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Till sessions" subtitle="Expected cash is the float plus cash taken, less cash refunded, by that cashier" />
      </div>
      {!tills.data ? (
        <Spinner />
      ) : tills.data.data.length === 0 ? (
        <p className="px-5 pb-6 text-sm text-slate-500">No till sessions.</p>
      ) : (
        <Table head={['Cashier', 'Location', 'Opened', 'Closed', 'Float', 'Expected', 'Counted', 'Variance', 'Status', '']} minWidth={900}>
          {tills.data.data.map((t) => (
            <tr key={t.id}>
              <Td>{t.cashier}</Td>
              <Td>{t.branch_name}</Td>
              <Td>{dateTime(t.opened_at, tz)}</Td>
              <Td>{t.closed_at ? dateTime(t.closed_at, tz) : '—'}</Td>
              <Td className="tabular-nums">{money(t.opening_float, c)}</Td>
              <Td className="tabular-nums">{t.expected_cash ? money(t.expected_cash, c) : '—'}</Td>
              <Td className="tabular-nums">{t.counted_cash ? money(t.counted_cash, c) : '—'}</Td>
              <Td className={cn('tabular-nums', t.variance && Number(t.variance) < 0 && 'font-semibold text-red-700')}>
                {t.variance ? money(t.variance, c) : '—'}
              </Td>
              <Td>{t.status === 'reconciled' ? <Badge tone="success">reconciled</Badge> : <StatusBadge status={t.status} />}</Td>
              <Td>
                {t.status === 'closed' && can('cash:reconcile') && (
                  <Button size="sm" variant="secondary" onClick={() => setReconciling(t)}>Reconcile</Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      )}
      {reconciling && <ReconcileModal till={reconciling} onClose={() => setReconciling(null)} />}
    </Card>
  );
}

function ReconcileModal({ till, onClose }: { till: TillRow; onClose: () => void }): ReactNode {
  const [note, setNote] = useState('');
  const reconcile = usePharmacyWrite<{ note: string }>('post', `/tills/${till.id}/reconcile`);
  return (
    <Modal open onClose={onClose} title={`Reconcile ${till.cashier}'s till`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">A till is reconciled by somebody other than the cashier who ran it.</p>
        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={reconcile.error} />
        <Button disabled={note.trim().length < 3} loading={reconcile.isPending} onClick={() => reconcile.mutate({ note }, { onSuccess: onClose })}>
          Reconcile
        </Button>
      </div>
    </Modal>
  );
}
